import { CheckOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons"; // Import icons
import type { InputRef } from "antd";
import { Button, Col, DatePicker, Form, Input, Row, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ContentBox } from "../../../components/layout/content";
import { LoginedLayout } from "../../../components/layout/loginLayout";
import { HeaderTitle } from "../../../components/styled-components";
import { UserContext } from "../../../context/userContext";
import { GET, POST } from "../../../services";
import { CANCEL_SHIPPING, GET_LIST_CHECKOUT } from "../../../services/path";
import { PlaySound } from "../../../functions/function";

interface DataType {
  logistic: string;
  trackingNo: string;
  orderNo: string;
  soNo: string;
  checkoutDate: string;
  checkoutStatus: number;
  isDuplicate: number;
}

interface ReturnPageProps {
  pageType: string;
}

const ReturnPage: React.FC<ReturnPageProps> = ({ pageType }) => {
  // Data state
  const [data, setData] = useState<DataType[]>([]);
  const [dataError, setDataerror] = useState<DataType[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [trackingInput, setTrackingInput] = useState<string>("");
  const { user } = useContext(UserContext);
  const inputRef = useRef<InputRef>(null);
  const tableStyle: React.CSSProperties = {
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)", // Soft shadow
    borderRadius: "8px", // Optional: Smooth corners
    overflow: "hidden", // Prevents shadow cutoff
  };

  const fetchDataTable = async () => {
    try {
      const result = await GET(GET_LIST_CHECKOUT(selectedDate, "cancel", pageType));
      if (result?.result?.length > 0) {
        message.success(`ดึงข้อมูลสำเร็จ`);
        const filteredData = result.result.filter((item: DataType) => item.checkoutStatus === 0);
        const filteredErrorData = result.result.filter(
          (item: DataType) => item.checkoutStatus === 1,
        );

        setData(filteredData);
        setDataerror(filteredErrorData);
      } else {
        message.warning(`ไม่มีข้อมูลที่ตรงกัน`);
      }
    } catch (error) {
      message.error(`พบข้อผิดพลาด`);
    }
  };

  const handleCheckout = async () => {
    const datacheckout = {
      trackingNo: trackingInput,
      warehouse: pageType,
      checkoutDate: dayjs().format("YYYY-MM-DD"),
      userCreate: user.user_id,
    };

    try {
      const resultCheckoutorder = await POST(CANCEL_SHIPPING, datacheckout);
      if (resultCheckoutorder.result.checkoutStatus !== 1) {
        PlaySound("success");
        setData((prevData) => [resultCheckoutorder.result, ...prevData]);
      } else {
        PlaySound("error");
        setDataerror((prevDataError) => [resultCheckoutorder.result, ...prevDataError]);
      }
    } catch (error) {
      PlaySound("error");
      message.error(`พบข้อผิดพลาด`);
    } finally {
      setTrackingInput(""); // Reset input after checkout
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date ? date.format("YYYY-MM-DD") : ""); // Set to empty string when cleared
  };

  useEffect(() => {
    if (selectedDate) {
      fetchDataTable();
    }
  }, [selectedDate]);

  const disableOtherDates = (current: Dayjs) => {
    const today = dayjs();
    const yesterday = today.subtract(1, "day");
    return !current.isSame(today, "day") && !current.isSame(yesterday, "day");
  };

  useEffect(() => {
    inputRef.current?.focus();
    window.scrollTo(0, 0);
  }, []);

  const columns: ColumnsType<DataType> = [
    { title: "Logistic", dataIndex: "logistic", key: "logistic" },
    { title: "Tracking No", dataIndex: "trackingNo", key: "trackingNo" },
    { title: "Order Number", dataIndex: "orderNo", key: "orderNumber" },
    {
      title: "Checked Out",
      key: "checkedOut",
      render: (_: any, record: DataType) =>
        record.checkoutStatus === 0 ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
    },
    { title: "SO", dataIndex: "soNo", key: "soNo" },
    { title: "Inv No", dataIndex: "invNo", key: "invNo" },
    {
      title: "Checkout Date",
      dataIndex: "checkoutDate",
      key: "checkoutDate",
      sorter: (a: DataType, b: DataType) =>
        new Date(a.checkoutDate).getTime() - new Date(b.checkoutDate).getTime(),
      defaultSortOrder: "descend",
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox style={{ backgroundColor: "#b6eefc" }}>
        <HeaderTitle>
          <h2>Cancel Checkout</h2>
        </HeaderTitle>
        <Form layout="vertical">
          <Row gutter={20}>
            <Col>
              <Form.Item label="Date">
                <DatePicker
                  value={selectedDate ? dayjs(selectedDate, "YYYY-MM-DD") : undefined} // Show undefined when cleared
                  onChange={handleDateChange}
                  format="DD-MM-YYYY"
                  disabledDate={disableOtherDates}
                  style={{ width: 150, marginBottom: 20 }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Reload">
                <Button onClick={fetchDataTable}>
                  <ReloadOutlined />
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Input
            ref={inputRef}
            placeholder="Enter Tracking No"
            value={trackingInput}
            onChange={(e) => setTrackingInput(e.target.value)}
            onPressEnter={handleCheckout}
            style={{ width: 400, marginRight: 10 }}
          />
        </div>
        <div style={{ padding: "20px", gap: "20px" }}>
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} md={12}>
              <h2>Duplicate</h2>
              <Table
                dataSource={dataError}
                columns={columns}
                rowKey="orderNumber"
                style={{ ...tableStyle, marginTop: 10 }}
                scroll={{ x: "max-content" }}
                onRow={(record) => ({
                  style: record.isDuplicate === 1 ? { backgroundColor: "yellow" } : {},
                })}
              />
            </Col>
            <Col xs={24} md={12}>
              <h2>Complete</h2>
              <Table
                dataSource={data}
                columns={columns}
                rowKey="orderNumber"
                style={{ ...tableStyle, marginTop: 10 }}
                scroll={{ x: "max-content" }}
              />
            </Col>
          </Row>
        </div>
      </ContentBox>
    </LoginedLayout>
  );
};

export default ReturnPage;
